// Shipments list — date / status / patient filter + free-text search.
// Click "New shipment" to start an exception flow.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useTenantHref } from '../lib/tenantPath.jsx';

const STATUS_OPTIONS = [
  '', 'draft', 'address_confirmed', 'weighed', 'rate_selected',
  'payment_confirmed', 'label_purchased', 'in_transit',
  'out_for_delivery', 'delivered', 'exception', 'canceled'
];

export default function Shipments() {
  const tHref = useTenantHref();
  const nav = useNavigate();
  const initial = useMemo(() => {
    const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 30);
    return { from: toDate(from), to: toDate(to) };
  }, []);
  const [from, setFrom] = useState(initial.from);
  const [to,   setTo]   = useState(initial.to);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function load() {
    setBusy(true); setErr(null);
    try {
      const params = {
        from: new Date(from).toISOString(),
        to:   new Date(to + 'T23:59:59').toISOString(),
      };
      if (status) params.status = status;
      if (search) params.q = search;
      const r = await api.shipments(params);
      setData(r);
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = data?.shipments || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Shipments</h1>
          <p className="text-sm text-slate-500">
            Ship-to-Patient exceptions: missed bedside or out-of-stock medications shipped to home.
          </p>
        </div>
        <button className="btn-primary" onClick={() => nav(tHref('/shipments/new'))}>+ New shipment</button>
      </div>

      <div className="card p-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <label className="block text-sm">
          <span className="text-xs text-slate-600">From</span>
          <input type="date" className="input" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">To</span>
          <input type="date" className="input" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="text-xs text-slate-600">Status</span>
          <select className="input" value={status} onChange={e => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All'}</option>)}
          </select>
        </label>
        <label className="block text-sm sm:col-span-2 lg:col-span-1">
          <span className="text-xs text-slate-600">Search (name / MRN / tracking)</span>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()} />
        </label>
        <button className="btn-primary" onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Apply'}</button>
      </div>

      {err && <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">{err}</div>}

      <div className="card overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2 text-left">Patient</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Carrier / Service</th>
              <th className="px-3 py-2 text-left">Tracking</th>
              <th className="px-3 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(s => (
              <tr key={s.id} className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => nav(tHref(`/shipments/${s.id}`))}>
                <td className="px-3 py-2 whitespace-nowrap">
                  {new Date(s.created_at).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{s.first_name} {s.last_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{s.mrn}</div>
                </td>
                <td className="px-3 py-2 text-xs">{s.reason}</td>
                <td className="px-3 py-2 text-xs">
                  {s.carrier || '—'}{s.service_level ? ` · ${s.service_level}` : ''}
                  {s.rate_amount_cents != null && (
                    <div className="text-slate-500">${(s.rate_amount_cents / 100).toFixed(2)}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs font-mono">{s.tracking_number || '—'}</td>
                <td className="px-3 py-2"><StatusBadge s={s.status} /></td>
              </tr>
            ))}
            {!rows.length && !busy && (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                No shipments in this window. Click <Link to={tHref('/shipments/new')} className="underline" style={{ color: 'var(--brand-primary)' }}>New shipment</Link> to start one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const tone =
    s === 'delivered' ? 'bg-emerald-100 text-emerald-800'
    : s === 'in_transit' || s === 'out_for_delivery' || s === 'label_purchased' ? 'bg-blue-100 text-blue-800'
    : s === 'exception' || s === 'canceled' ? 'bg-red-100 text-red-800'
    : 'bg-slate-100 text-slate-700';
  return <span className={`px-2 py-0.5 rounded text-xs ${tone}`}>{s}</span>;
}
function toDate(d) {
  const tz = d.getTimezoneOffset();
  const dd = new Date(d.getTime() - tz * 60 * 1000);
  return dd.toISOString().slice(0, 10);
}
